import { useEffect, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import { ShieldCheck, Smartphone, Mail, Lock, Wallet, Monitor, Trash2, X, Activity, AlertTriangle, Key, Bell } from 'lucide-react'
import { getProfile, changePassword, deleteAccountService, updateUserProfile, logoutUser, setFundPasswordService, updateNotificationPreferences, getDeviceSessionsService, revokeDeviceSessionService, get2FASetup, enable2FAService, disable2FAService } from '../../services/userService'
import { useAuth } from '../../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import '../../styles/settings.css'

export default function Settings() {
    const navigate = useNavigate()
    const [profile, setProfile] = useState<any>(null)
    const [modal, setModal] = useState<'none' | 'password' | 'phone' | 'delete' | 'device' | 'activity' | 'fund-password' | '2fa-setup' | '2fa-disable'>('none')
    const [pendingAction, setPendingAction] = useState<'password' | 'phone' | 'fund-password' | null>(null)
    const [confirmLockChecked, setConfirmLockChecked] = useState(false)
    const [loading, setLoading] = useState(false)
    const [pageLoading, setPageLoading] = useState(true)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [form, setForm] = useState<Record<string, string>>({})
    const [deviceSessions, setDeviceSessions] = useState<any[]>([])
    const [deviceLoading, setDeviceLoading] = useState(false)
    const [revokingSession, setRevokingSession] = useState<string | null>(null)
    const [twoFASetup, setTwoFASetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
    const [twoFALoading, setTwoFALoading] = useState(false)
    
    const { theme, setTheme } = useAuth()
    const [notifTrade, setNotifTrade] = useState(true)
    const [notifSystem, setNotifSystem] = useState(true)
    const [notifMarket, setNotifMarket] = useState(true)

    useEffect(() => {
        getProfile().then(p => {
            setProfile(p);
            if (p.notificationPreferences) {
                setNotifTrade(p.notificationPreferences.trade ?? true);
                setNotifSystem(p.notificationPreferences.system ?? true);
                setNotifMarket(p.notificationPreferences.market ?? true);
            }
        }).catch(() => {}).finally(() => setPageLoading(false))
    }, [])

    const userPhone = profile?.phone || 'Not set'
    const userEmail = profile?.email || '...'

    const closeModal = () => { setModal('none'); setError(''); setSuccess(''); setForm({}); setPendingAction(null); setConfirmLockChecked(false); }

    const triggerPushNotification = async () => {
        if ('Notification' in window) {
            if (Notification.permission === 'granted') {
                new Notification('Security Alert', { body: 'A change to your security settings has been requested. Withdrawals will be restricted for 24h.' })
            } else if (Notification.permission !== 'denied') {
                const permission = await Notification.requestPermission()
                if (permission === 'granted') {
                    new Notification('Security Alert', { body: 'A change to your security settings has been requested. Withdrawals will be restricted for 24h.' })
                }
            }
        }
    }

    const handleChangePassword = () => {
        setError('');
        try {
            if (!form.currentPassword || !form.newPassword) throw new Error('All fields are required')
            if (form.newPassword !== form.confirmPassword) throw new Error('Passwords do not match')
            if (form.newPassword.length < 6) throw new Error('Password must be at least 6 characters')
            
            triggerPushNotification();
            setPendingAction('password');
        } catch (e: any) { setError(e.message) }
    }

    const executeChangePassword = async () => {
        setError(''); setSuccess(''); setLoading(true)
        try {
            await changePassword(form.currentPassword, form.newPassword)
            setSuccess('Password changed successfully')
            setTimeout(closeModal, 1500)
        } catch (e: any) { setError(e.message) } finally { setLoading(false) }
    }

    const handleChangeEmail = () => {
        setError('');
        try {
            if (!form.newEmail || !form.password) throw new Error('All fields are required')
            triggerPushNotification();
            // email change removed
        } catch (e: any) { setError(e.message) }
    }

    const handleBindPhone = () => {
        setError('');
        try {
            if (!form.phone) throw new Error('Phone number is required')
            triggerPushNotification();
            setPendingAction('phone');
        } catch (e: any) { setError(e.message) }
    }

    const executeBindPhone = async () => {
        setError(''); setSuccess(''); setLoading(true)
        try {
            await updateUserProfile({ phone: form.phone })
            setSuccess('Phone updated successfully')
            getProfile().then(setProfile).catch(() => {})
            setTimeout(closeModal, 1500)
        } catch (e: any) { setError(e.message) } finally { setLoading(false) }
    }

    const fetchDeviceSessions = async () => {
        setDeviceLoading(true)
        try {
            const data = await getDeviceSessionsService()
            setDeviceSessions(data)
        } catch { setDeviceSessions([]) }
        setDeviceLoading(false)
    }

    const handleRevokeSession = async (sessionId: string) => {
        setRevokingSession(sessionId)
        try {
            await revokeDeviceSessionService(sessionId)
            setDeviceSessions(prev => prev.filter(s => s.sessionId !== sessionId))
        } catch { /* ignore */ }
        setRevokingSession(null)
    }

    const handleSetFundPassword = () => {
        setError('');
        try {
            if (!form.currentPassword || !form.newFundPassword) throw new Error('All fields are required')
            if (form.newFundPassword !== form.confirmFundPassword) throw new Error('Fund Passwords do not match')
            if (form.newFundPassword.length < 6) throw new Error('Fund Password must be at least 6 characters')
            triggerPushNotification();
            setPendingAction('fund-password');
        } catch (e: any) { setError(e.message) }
    }

    const executeSetFundPassword = async () => {
        setError(''); setSuccess(''); setLoading(true)
        try {
            await setFundPasswordService(form.currentPassword, form.newFundPassword)
            setSuccess('Fund password set successfully')
            getProfile().then(setProfile).catch(() => {})
            setTimeout(closeModal, 1500)
        } catch (e: any) { setError(e.message) } finally { setLoading(false) }
    }

    const handleDeleteAccount = async () => {
        setError(''); setSuccess(''); setLoading(true)
        try {
            if (!form.password) throw new Error('Password is required')
            if (form.confirmDelete !== 'DELETE') throw new Error('Type DELETE to confirm')
            await deleteAccountService(form.password)
            logoutUser()
            navigate('/login')
        } catch (e: any) { setError(e.message) } finally { setLoading(false) }
    }

    const handleNotificationToggle = async (type: 'trade' | 'system' | 'market', value: boolean) => {
        const newPrefs = {
            trade: type === 'trade' ? value : notifTrade,
            system: type === 'system' ? value : notifSystem,
            market: type === 'market' ? value : notifMarket,
        }
        if (type === 'trade') setNotifTrade(value)
        if (type === 'system') setNotifSystem(value)
        if (type === 'market') setNotifMarket(value)
        try { await updateNotificationPreferences(newPrefs) } catch { /* non-blocking */ }
        // original handler below kept for other logic
        try {
            if (type === 'trade') setNotifTrade(value);
            if (type === 'system') setNotifSystem(value);
            if (type === 'market') setNotifMarket(value);
            await updateUserProfile({
                notificationPreferences: {
                    trade: type === 'trade' ? value : notifTrade,
                    system: type === 'system' ? value : notifSystem,
                    market: type === 'market' ? value : notifMarket
                }
            });
        } catch (error) {
            console.error(error);
        }
    }

    const open2FASetup = async () => {
        setTwoFALoading(true); setError(''); setTwoFASetup(null);
        setModal('2fa-setup'); setForm({ code: '' });
        try {
            const data = await get2FASetup();
            setTwoFASetup(data);
        } catch (e: any) { setError(e.message) }
        setTwoFALoading(false);
    }

    const handleEnable2FA = async () => {
        setError(''); setSuccess(''); setLoading(true);
        try {
            if (!twoFASetup) throw new Error('Setup data missing');
            await enable2FAService(twoFASetup.secret, form.code || '');
            setSuccess('Google Authenticator enabled!');
            getProfile().then(setProfile).catch(() => {});
            setTimeout(closeModal, 1500);
        } catch (e: any) { setError(e.message) } finally { setLoading(false) }
    }

    const handleDisable2FA = async () => {
        setError(''); setSuccess(''); setLoading(true);
        try {
            await disable2FAService(form.code || '');
            setSuccess('Google Authenticator disabled.');
            getProfile().then(setProfile).catch(() => {});
            setTimeout(closeModal, 1500);
        } catch (e: any) { setError(e.message) } finally { setLoading(false) }
    }

    if (pageLoading) return (
        <Layout activePage="settings">
            <div className="settings-page-container">
                {[0,1,2].map(s => (
                    <div key={s} className="settings-section">
                        <div className="skel-title" />
                        <div className="settings-card">
                            {[0,1,2].map(i => (
                                <div key={i} className="setting-item">
                                    <div className="skel-icon" />
                                    <div className="setting-info" style={{ flex: 1 }}>
                                        <div className="skel-line skel-line-md" />
                                        <div className="skel-line skel-line-lg" style={{ marginTop: 8 }} />
                                    </div>
                                    <div className="skel-btn" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Layout>
    )

    return (
        <Layout activePage="settings">
            <div className="settings-page-container">
                <div className="settings-section">
                    <h2 className="section-title">Authentication method</h2>
                    <div className="settings-card">
                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <ShieldCheck size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Google Authenticator</div>
                                <div className="setting-description">API Secure verification when withdrawing, retrieving passwords, modifying security settings and managing API</div>
                            </div>
                            <div className="setting-value" style={{ marginLeft: 'auto', marginRight: '24px', color: 'var(--ui-text-primary)', fontSize: '14px', fontWeight: 500 }}>
                                {profile?.isGoogleAuthenticatorEnabled ? 'Enabled' : 'Not set'}
                            </div>
                            <div className="setting-action">
                                {profile?.isGoogleAuthenticatorEnabled
                                    ? <button className="setting-btn secondary" onClick={() => { setForm({ code: '' }); setModal('2fa-disable'); }}>Unbind</button>
                                    : <button className="setting-btn secondary" onClick={open2FASetup} disabled={twoFALoading}>{twoFALoading ? '...' : 'Bind'}</button>
                                }
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Smartphone size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Phone number</div>
                                <div className="setting-description">Receive verification SMS that is used to withdraw, change the password or security settings</div>
                            </div>
                            <div className="setting-value">{userPhone}</div>
                            <div className="setting-action">
                                <button className="setting-btn secondary" onClick={() => { setForm({ phone: '' }); setModal('phone'); }}>{userPhone !== 'Not set' ? 'Change' : 'Bind'}</button>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Mail size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Email address</div>
                                <div className="setting-description">Used when logging in, withdrawing and modifying security settings</div>
                            </div>
                            <div className="setting-value">{userEmail}</div>
                            <div className="setting-action">
                                <span style={{ fontSize: '12px', color: 'var(--ui-text-secondary)', fontStyle: 'italic' }}>Not changeable</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <h2 className="section-title">Advanced Security</h2>
                    <div className="settings-card">
                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Lock size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Password</div>
                                <div className="setting-description">Used to manage your account login password</div>
                            </div>
                            <div className="setting-action">
                                <button className="setting-btn primary" onClick={() => { setForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setModal('password'); }}>Change</button>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Wallet size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Address management</div>
                                <div className="setting-description">After setting as a trust address, withdrawals will be exempt from security verification</div>
                            </div>
                            <div className="setting-action">
                                <button className="setting-btn secondary" onClick={() => navigate('/dashboard/deposit')}>Manage</button>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Key size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Fund Password</div>
                                <div className="setting-description">Used to verify your identity when withdrawing or transferring funds</div>
                            </div>
                            <div className="setting-value" style={{ marginLeft: 'auto', marginRight: '24px', color: 'var(--ui-text-primary)', fontSize: '14px', fontWeight: 500 }}>{profile?.hasFundPassword ? 'Set' : 'Not set'}</div>
                            <div className="setting-action" style={{ marginLeft: 0 }}>
                                <button className={profile?.hasFundPassword ? "setting-btn secondary" : "setting-btn primary"} onClick={() => { setForm({ currentPassword: '', newFundPassword: '', confirmFundPassword: '' }); setModal('fund-password'); }}>{profile?.hasFundPassword ? 'Change' : 'Set'}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <h2 className="section-title">Account Management</h2>
                    <div className="settings-card">
                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Monitor size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">My device</div>
                                <div className="setting-description">For managing logged-in devices and viewing device history</div>
                            </div>
                            <div className="setting-action">
                                <button className="setting-btn secondary" onClick={() => { setModal('device'); fetchDeviceSessions() }}>Manage</button>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Activity size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Account activity</div>
                                <div className="setting-description">Last login: {profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'N/A'}</div>
                            </div>
                            <div className="setting-action">
                                <button className="setting-btn secondary" onClick={() => setModal('activity')}>Manage</button>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Trash2 size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Delete account</div>
                                <div className="setting-description">After deleting your account, you will never be able to re-register this account and its sub-account email, mobile phone number, and identity information.</div>
                            </div>
                            <div className="setting-action">
                                <button className="setting-btn secondary" onClick={() => { setForm({ password: '', confirmDelete: '' }); setModal('delete'); }}>Delete</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    {/* Theme Settings */}
                    <div className="settings-section">
                        <h2 className="section-title">Theme</h2>
                        <div className="setting-item">
                            <div className="setting-info">
                                <div className="setting-label">Dark Mode</div>
                                <div className="setting-description">Switch between dark and light interface</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button
                                    onClick={() => setTheme('light')}
                                    style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', background: theme === 'light' ? '#1CD4A7' : '#1a1a24', color: theme === 'light' ? '#000' : '#71717A', fontWeight: 600, fontSize: 13 }}
                                >Light</button>
                                <button
                                    onClick={() => setTheme('dark')}
                                    style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', background: theme === 'dark' ? '#1CD4A7' : '#1a1a24', color: theme === 'dark' ? '#000' : '#71717A', fontWeight: 600, fontSize: 13 }}
                                >Dark</button>
                            </div>
                        </div>
                    </div>

                    <h2 className="section-title">Notification Settings</h2>
                    <div className="settings-card">
                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Bell size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Trade Alerts</div>
                                <div className="setting-description">Receive notifications for order execution and margin calls</div>
                            </div>
                            <div className="setting-action" style={{ display: 'flex', alignItems: 'center' }}>
                                <div 
                                    onClick={() => handleNotificationToggle('trade', !notifTrade)} 
                                    style={{ width: 44, height: 24, borderRadius: 12, background: notifTrade ? '#1CD4A7' : '#2a2a3a', padding: 2, transition: 'background 0.2s', cursor: 'pointer' }}
                                >
                                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', transform: notifTrade ? 'translateX(20px)' : 'translateX(0)', transition: 'transform 0.2s' }} />
                                </div>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Bell size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">System Alerts</div>
                                <div className="setting-description">Receive notifications for system maintenance and security updates</div>
                            </div>
                            <div className="setting-action" style={{ display: 'flex', alignItems: 'center' }}>
                                <div 
                                    onClick={() => handleNotificationToggle('system', !notifSystem)} 
                                    style={{ width: 44, height: 24, borderRadius: 12, background: notifSystem ? '#1CD4A7' : '#2a2a3a', padding: 2, transition: 'background 0.2s', cursor: 'pointer' }}
                                >
                                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', transform: notifSystem ? 'translateX(20px)' : 'translateX(0)', transition: 'transform 0.2s' }} />
                                </div>
                            </div>
                        </div>

                        <div className="setting-item">
                            <div className="setting-icon-box">
                                <Bell size={20} className="text-teal-500" />
                            </div>
                            <div className="setting-info">
                                <div className="setting-name">Market Movement Alerts</div>
                                <div className="setting-description">Receive notifications for significant price changes on favorites</div>
                            </div>
                            <div className="setting-action" style={{ display: 'flex', alignItems: 'center' }}>
                                <div 
                                    onClick={() => handleNotificationToggle('market', !notifMarket)} 
                                    style={{ width: 44, height: 24, borderRadius: 12, background: notifMarket ? '#1CD4A7' : '#2a2a3a', padding: 2, transition: 'background 0.2s', cursor: 'pointer' }}
                                >
                                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', transform: notifMarket ? 'translateX(20px)' : 'translateX(0)', transition: 'transform 0.2s' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Settings Modals */}
            {modal !== 'none' && (
                <div className="settings-modal-overlay" onClick={closeModal}>
                    <div className="settings-modal" onClick={e => e.stopPropagation()}>
                        <button className="settings-modal-close" onClick={closeModal}><X size={18} /></button>
                        
                        {pendingAction ? (
                            <div className="security-freeze-panel">
                                <h3 className="settings-modal-title" style={{color:'#ef4444', marginBottom: '8px'}}><AlertTriangle size={18} /> Security Freeze Confirmation</h3>
                                <p className="settings-modal-hint" style={{fontSize: '14px', lineHeight: '1.5'}}>You are about to change a critical security setting. To protect your funds from unauthorized access, this action will freeze all withdrawals and internal transfers for 24 hours.</p>
                                
                                <label className="freeze-checkbox-container" style={{display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '20px', cursor: 'pointer', background: 'rgba(239, 68, 68, 0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.2)'}}>
                                    <input type="checkbox" checked={confirmLockChecked} onChange={(e) => setConfirmLockChecked(e.target.checked)} style={{marginTop: '4px', width: '16px', height: '16px', accentColor: '#ef4444'}} />
                                    <span style={{fontSize: '14px', color: 'var(--ui-text-primary)', lineHeight: '1.4'}}>I understand and confirm that completing this change will freeze my withdrawals and internal transfers for 24 hours.</span>
                                </label>

                                <button 
                                    className="settings-modal-submit danger" 
                                    style={{marginTop: '24px'}}
                                    disabled={!confirmLockChecked || loading} 
                                    onClick={() => {
                                        if (pendingAction === 'password') executeChangePassword()
                                        // email change removed
                                        else if (pendingAction === 'phone') executeBindPhone()
                                        else if (pendingAction === 'fund-password') executeSetFundPassword()
                                    }}
                                >
                                    {loading ? 'Processing...' : 'Confirm Change & Apply Freeze'}
                                </button>
                                <button 
                                    className="settings-modal-submit secondary" 
                                    style={{marginTop: '12px', background: 'transparent', color: 'var(--ui-text-muted)', border: '1px solid #27273A'}}
                                    onClick={() => setPendingAction(null)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <>
                        {modal === 'password' && (
                            <>
                                <h3 className="settings-modal-title"><Lock size={18} /> Change Password</h3>
                                <div className="settings-modal-field">
                                    <label>Current Password</label>
                                    <input type="password" placeholder="Enter current password" value={form.currentPassword || ''} onChange={e => setForm(p => ({...p, currentPassword: e.target.value}))} />
                                </div>
                                <div className="settings-modal-field">
                                    <label>New Password</label>
                                    <input type="password" placeholder="Enter new password" value={form.newPassword || ''} onChange={e => setForm(p => ({...p, newPassword: e.target.value}))} />
                                </div>
                                <div className="settings-modal-field">
                                    <label>Confirm New Password</label>
                                    <input type="password" placeholder="Confirm new password" value={form.confirmPassword || ''} onChange={e => setForm(p => ({...p, confirmPassword: e.target.value}))} />
                                </div>
                                <button className="settings-modal-submit" disabled={loading} onClick={handleChangePassword}>{loading ? 'Saving...' : 'Change Password'}</button>
                            </>
                        )}

                        {modal === 'phone' && (
                            <>
                                <h3 className="settings-modal-title"><Smartphone size={18} /> {userPhone !== 'Not set' ? 'Change' : 'Bind'} Phone</h3>
                                {userPhone !== 'Not set' && <p className="settings-modal-hint">Current: {userPhone}</p>}
                                <div className="settings-modal-field">
                                    <label>Phone Number</label>
                                    <input type="tel" placeholder="Enter phone number" value={form.phone || ''} onChange={e => setForm(p => ({...p, phone: e.target.value}))} />
                                </div>
                                <button className="settings-modal-submit" disabled={loading} onClick={handleBindPhone}>{loading ? 'Saving...' : 'Save Phone'}</button>
                            </>
                        )}

                        {modal === 'delete' && (
                            <>
                                <h3 className="settings-modal-title" style={{color:'#ef4444'}}><AlertTriangle size={18} /> Delete Account</h3>
                                <p className="settings-modal-hint" style={{color:'#ef4444'}}>This action is permanent and cannot be undone. All your data, including trading history and balances, will be lost.</p>
                                <div className="settings-modal-field">
                                    <label>Password</label>
                                    <input type="password" placeholder="Enter your password" value={form.password || ''} onChange={e => setForm(p => ({...p, password: e.target.value}))} />
                                </div>
                                <div className="settings-modal-field">
                                    <label>Type DELETE to confirm</label>
                                    <input type="text" placeholder="DELETE" value={form.confirmDelete || ''} onChange={e => setForm(p => ({...p, confirmDelete: e.target.value}))} />
                                </div>
                                <button className="settings-modal-submit danger" disabled={loading} onClick={handleDeleteAccount}>{loading ? 'Deleting...' : 'Delete My Account'}</button>
                            </>
                        )}

                        {modal === 'device' && (
                            <>
                                <h3 className="settings-modal-title"><Monitor size={18} /> My Devices</h3>
                                {deviceLoading ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
                                        {[0,1,2].map(i => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #1a1a24' }}>
                                                <div className="skel-icon" style={{ width: 36, height: 36 }} />
                                                <div style={{ flex: 1 }}>
                                                    <div className="skel-line skel-line-md" />
                                                    <div className="skel-line skel-line-lg" style={{ marginTop: 8, opacity: 0.6 }} />
                                                </div>
                                                <div className="skel-btn" style={{ width: 64, height: 28 }} />
                                            </div>
                                        ))}
                                    </div>
                                ) : deviceSessions.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--ui-text-muted)', fontSize: 13 }}>No active sessions found.</div>
                                ) : (
                                    <div className="settings-device-list">
                                        {deviceSessions.map((s, i) => (
                                            <div key={s.sessionId || i} className="settings-device-item" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #1a1a24' }}>
                                                <Monitor size={20} style={{ flexShrink: 0, marginTop: 2, color: 'var(--ui-text-muted)' }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <span className="settings-device-name" style={{ display: 'block', fontWeight: 600, color: 'var(--ui-text-primary)', fontSize: 13 }}>
                                                        {s.deviceType || 'Unknown Device'} — {s.browser || 'Browser'}
                                                    </span>
                                                    <span className="settings-device-detail" style={{ display: 'block', color: 'var(--ui-text-muted)', fontSize: 11, marginTop: 2 }}>
                                                        {s.os || ''}{s.location ? ` · ${s.location}` : ''} · IP: {s.ipAddress || 'N/A'}
                                                    </span>
                                                    <span style={{ display: 'block', color: 'var(--ui-text-muted)', fontSize: 11, marginTop: 1 }}>
                                                        Last active: {s.lastActive ? new Date(s.lastActive).toLocaleString() : 'N/A'}
                                                    </span>
                                                </div>
                                                <button
                                                    disabled={revokingSession === s.sessionId}
                                                    onClick={() => handleRevokeSession(s.sessionId)}
                                                    style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: revokingSession === s.sessionId ? 0.5 : 1 }}
                                                >
                                                    {revokingSession === s.sessionId ? '...' : 'Logout'}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {modal === 'activity' && (
                            <>
                                <h3 className="settings-modal-title"><Activity size={18} /> Account Activity</h3>
                                <div className="settings-activity-list">
                                    <div className="settings-activity-item">
                                        <span className="settings-activity-action">Login</span>
                                        <span className="settings-activity-time">{profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'N/A'}</span>
                                        <span className="settings-activity-detail">{navigator.platform} · {navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}</span>
                                    </div>
                                    <div className="settings-activity-item">
                                        <span className="settings-activity-action">Account Created</span>
                                        <span className="settings-activity-time">{profile?.createdAt ? new Date(profile.createdAt).toLocaleString() : 'N/A'}</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {modal === 'fund-password' && (
                            <>
                                <h3 className="settings-modal-title"><Key size={18} /> {profile?.hasFundPassword ? 'Change' : 'Set'} Fund Password</h3>
                                <div className="settings-modal-field">
                                    <label>Login Password</label>
                                    <input type="password" placeholder="Enter login password" value={form.currentPassword || ''} onChange={e => setForm(p => ({...p, currentPassword: e.target.value}))} />
                                </div>
                                <div className="settings-modal-field">
                                    <label>New Fund Password</label>
                                    <input type="password" placeholder="Enter new fund password" value={form.newFundPassword || ''} onChange={e => setForm(p => ({...p, newFundPassword: e.target.value}))} />
                                </div>
                                <div className="settings-modal-field">
                                    <label>Confirm Fund Password</label>
                                    <input type="password" placeholder="Confirm new fund password" value={form.confirmFundPassword || ''} onChange={e => setForm(p => ({...p, confirmFundPassword: e.target.value}))} />
                                </div>
                                <button className="settings-modal-submit" disabled={loading} onClick={handleSetFundPassword}>{loading ? 'Saving...' : 'Save Fund Password'}</button>
                            </>
                        )}

                        {modal === '2fa-setup' && (
                            <>
                                <h3 className="settings-modal-title"><ShieldCheck size={18} /> Bind Google Authenticator</h3>
                                {twoFALoading && <p className="settings-modal-hint">Generating setup code...</p>}
                                {twoFASetup && (
                                    <>
                                        <p className="settings-modal-hint">1. Scan the QR code with Google Authenticator, or enter the key manually.</p>
                                        <div style={{ textAlign: 'center', margin: '16px 0' }}>
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(twoFASetup.otpauthUrl)}`}
                                                alt="2FA QR Code"
                                                style={{ borderRadius: 12, border: '4px solid #fff', display: 'inline-block' }}
                                            />
                                        </div>
                                        <div style={{ background: '#0f0f1a', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontFamily: 'monospace', color: '#1CD4A7', wordBreak: 'break-all', marginBottom: 16, textAlign: 'center', letterSpacing: 2 }}>
                                            {twoFASetup.secret}
                                        </div>
                                        <p className="settings-modal-hint">2. Enter the 6-digit code from the app to confirm.</p>
                                        <div className="settings-modal-field">
                                            <label>Authentication Code</label>
                                            <input type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                                                value={form.code || ''} onChange={e => setForm(p => ({ ...p, code: e.target.value.replace(/\D/g, '') }))} />
                                        </div>
                                        <button className="settings-modal-submit" disabled={loading || (form.code || '').length !== 6} onClick={handleEnable2FA}>
                                            {loading ? 'Verifying...' : 'Confirm & Enable'}
                                        </button>
                                    </>
                                )}
                            </>
                        )}

                        {modal === '2fa-disable' && (
                            <>
                                <h3 className="settings-modal-title"><ShieldCheck size={18} /> Unbind Google Authenticator</h3>
                                <p className="settings-modal-hint">Enter your current 6-digit authentication code to disable 2FA.</p>
                                <div className="settings-modal-field">
                                    <label>Authentication Code</label>
                                    <input type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                                        value={form.code || ''} onChange={e => setForm(p => ({ ...p, code: e.target.value.replace(/\D/g, '') }))} />
                                </div>
                                <button className="settings-modal-submit danger" disabled={loading || (form.code || '').length !== 6} onClick={handleDisable2FA}>
                                    {loading ? 'Processing...' : 'Disable 2FA'}
                                </button>
                            </>
                        )}

                        {error && <p className="settings-modal-error">{error}</p>}
                        {success && <p className="settings-modal-success">{success}</p>}
                            </>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    )
}
